import {
  Dropdown,
  DropdownTriggerButton,
  DropdownContent,
  DropdownCheckboxItem,
  DropdownEmpty,
  DropdownLoading,
} from '@/components';
import { SlidersHorizontal } from 'lucide-react';

export interface MultiSelectFilterProps {
  items: Record<number, string>;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  buttonLabel: string;
  emptyLabel: string;
  isLoading?: boolean;
}

// Renders a popup with a checkbox per item, letting the user select any number of them.
// The trigger button shows a count badge while a selection is active. Labels are passed
// in (already translated) so the component stays feature-agnostic and reusable.
export function MultiSelectFilter({
  items,
  selectedIds,
  onChange,
  buttonLabel,
  emptyLabel,
  isLoading = false,
}: MultiSelectFilterProps) {
  const selectedCount = selectedIds.length;

  // Add the id when it is missing, otherwise remove it.
  const handleToggle = (id: number) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  return (
    <Dropdown>
      <DropdownTriggerButton disabled={isLoading}>
        <SlidersHorizontal className="shrink-0 size-3.5" />

        {buttonLabel}

        {selectedCount > 0 && (
          <span className="font-medium text-[10px] py-0.5 px-[5px] bg-primary text-onprimary leading-3 rounded-full">
            {selectedCount}
          </span>
        )}
      </DropdownTriggerButton>

      <DropdownContent>
        {isLoading ? (
          <DropdownLoading />
        ) : Object.keys(items).length === 0 ? (
          <DropdownEmpty>{emptyLabel}</DropdownEmpty>
        ) : (
          Object.entries(items).map(([id, label]) => {
            const numericId = Number(id);
            return (
              <DropdownCheckboxItem
                key={id}
                checked={selectedIds.includes(numericId)}
                onChange={() => handleToggle(numericId)}
              >
                {label}
              </DropdownCheckboxItem>
            );
          })
        )}
      </DropdownContent>
    </Dropdown>
  );
}
